import { ValidatorFn } from "@angular/forms";
import { CategoryModelFolder } from "../../category-builder/category-builder.models";
import { ContextKey } from "../../context/state/context.model";

export interface Model {
  uuid: string;
  label: string;
  description: string;
  model_type: ModelType;
  working_version: string;
  released_version: string;
  released_message: string;
  obsolete?: boolean;
  versions: number;
  created: Date;
  version_data?: any;
  is_default: boolean;
}

export interface ModelRequest {
  model_label: string;
  description: string;
  model_type: string;
  workspace: string;
}

export interface ModelDetailsViewModel {
  modelId: string;
  label: string;
  description: string;
  whereUsed: string[];
  hasMoreWhereUsed: boolean;
  lastApiRequestRelative: string | null;
  lastApiRequestExact: string | null;
  versions: number;
  releasedLabel: string;
  isDefault: boolean;
  isArchived: boolean;
}

export interface ModelState {
  summaries: Model[];
  versions: ModelVersion[];
  loadingModels: boolean;
  loadingVersions: boolean;
  activatedModelVersions: ModelVersion[];
  modelReleaseHistory: ModelReleaseHistory[];
  error: string;
}

export enum ModelType {
  CATEGORY = "category-schema",
  SURVEY_SENTIMENT = "sentiment-prediction-schema",
  CHAT_SENTIMENT = "chat-sentiment-prediction-schema",
  SURVEY_TOPIC = "topic-prediction-schema",
  EMOTION = "emotion-prediction-schema",
  SURVEY_INTENT = "intent-prediction-schema",
  CHAT_TOPIC = "chat-topic-prediction-schema",
  CHAT_INTENT = "chat-intent-prediction-schema",
  LEXICON_SENTIMENT = "sentiment-lexicon-schema",
}

export enum CommonModelType {
  CATEGORY = "category",
  SENTIMENT = "sentiment",
  TOPIC = "topic",
  EMOTION = "emotion",
  INTENT = "intent",
}

export const MODEL_TYPE_TO_COMMON_TYPE = {
  [ModelType.CATEGORY]: CommonModelType.CATEGORY,
  [ModelType.SURVEY_TOPIC]: CommonModelType.TOPIC,
  [ModelType.CHAT_TOPIC]: CommonModelType.TOPIC,
  [ModelType.EMOTION]: CommonModelType.EMOTION,
  [ModelType.SURVEY_INTENT]: CommonModelType.INTENT,
  [ModelType.CHAT_INTENT]: CommonModelType.INTENT,
  [ModelType.SURVEY_SENTIMENT]: CommonModelType.SENTIMENT,
  [ModelType.CHAT_SENTIMENT]: CommonModelType.SENTIMENT,
  [ModelType.LEXICON_SENTIMENT]: CommonModelType.SENTIMENT,
};

export const COMMON_MODEL_TO_CONTEXT_KEY = {
  [CommonModelType.SENTIMENT]: ContextKey.SentimentContextKey,
  [CommonModelType.TOPIC]: ContextKey.TopicContextKey,
  [CommonModelType.EMOTION]: ContextKey.EmotionContextKey,
  [CommonModelType.INTENT]: ContextKey.IntentContextKey,
  [CommonModelType.CATEGORY]: ContextKey.CategoryContextKey,
};

export const MODEL_TYPE_TO_CONTEXT_KEY = (modelType: ModelType) => {
  return COMMON_MODEL_TO_CONTEXT_KEY[MODEL_TYPE_TO_COMMON_TYPE[modelType]];
};

export const sentimentFilterFunction = (model: Model) =>
  [
    ModelType.CHAT_SENTIMENT,
    ModelType.SURVEY_SENTIMENT,
    ModelType.LEXICON_SENTIMENT,
  ].some((type) => type === model.model_type);

export const topicFilterFunction = (model: Model) =>
  [ModelType.CHAT_TOPIC, ModelType.SURVEY_TOPIC].some(
    (type) => type === model.model_type,
  );

export const emotionFilterFunction = (model: Model) =>
  ModelType.EMOTION === model.model_type;

export const intentFilterFunction = (model: Model) =>
  [ModelType.CHAT_INTENT, ModelType.SURVEY_INTENT].some(
    (type) => type === model.model_type,
  );

export const categoryFilterFunction = (model: Model) =>
  ModelType.CATEGORY === model.model_type;

export const commonModelTypeToFilterFunction = {
  [CommonModelType.SENTIMENT]: sentimentFilterFunction,
  [CommonModelType.TOPIC]: topicFilterFunction,
  [CommonModelType.EMOTION]: emotionFilterFunction,
  [CommonModelType.INTENT]: intentFilterFunction,
  [CommonModelType.CATEGORY]: categoryFilterFunction,
};

export interface ModelVersion {
  uuid: string;
  version_number: number;
  model_id: string;
  message: string;
  created: Date;
  updated: Date;
  version_data: ModelVersionData;
  release_context: ReleaseContext;
  options: ModelVersionOptions;
}

export interface ReleaseContext {
  [commonModelType: string]: { modelId: string; versionId: string };
}

export interface ModelVersionData {
  ml_config_id?: string;
  lexicon_config_id?: string;
  category_tree?: CategoryModelFolder[];
}

export interface ModelVersionOptions {
  category_verbatim_exclusions: { [candidate: string]: string[] };
}

export interface GenericDialog {
  title: string;
  fields?: GenericField[];
}

export interface GenericField {
  name: string;
  label: string;
  default?: any;
  options?: { name: string; value: any }[];
  validators?: ValidatorFn[];
  hints?: string[];
}

export interface ModelReleaseHistory {
  uuid: string;
  model_version_id: string;
  released: Date;
  version_number: number;
}

export class ModelsApplied {
  sentiment: AppliedModel[];
  intent: AppliedModel[];
  emotion: AppliedModel[];
  topic: AppliedModel[];
  category: AppliedModel[];
}

export class AppliedModel {
  model_id: string;
  versions: AppliedVersion[];
}

export class AppliedVersion {
  applied_at: number;
  version_id: string;
}

export class SelectedModelVersion {
  model: string;
  version: string;
  model_type?: string;
}

interface WordDetails {
  start: number;
  length: number;
  type: string;
  raw_text: string;
}

export interface GroupDetails {
  start: number;
  length: number;
  valence: number;
  words: WordDetails[];
}

export interface SentimentDetails {
  predict: number;
  confidence: number;
  document_id: string;
  model: { model_id: string; version_id: string }[];
  details: GroupDetails[];
}
